import { useState } from "react";

import { Clock } from "lucide-react";
import { useDispatch } from "react-redux";

import { showError, showSuccess } from "@/utils/notification";

import { ReportGeneratorTemplateWithTypeSchema } from "@api/generated/types.gen";

import fileWithTrashIcon from "@assets/images/file-with-trash.png";
import { ReactComponent as StateIcon } from "@assets/svg/arrows-swap.svg";
import { ReactComponent as SectionsIcon } from "@assets/svg/box-stack.svg";
import { ReactComponent as EditIcon } from "@assets/svg/document-with-pen.svg";
import { ReactComponent as DeleteIcon } from "@assets/svg/trash-red.svg";

import { ConfirmationModal } from "@components/ConfirmationModal";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@components/ui/accordion";
import { Button } from "@components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@components/ui/card";

import { useDeleteTemplate } from "@hooks/mutations/use-templates";

import { formatDateToLocal } from "@utils/dateUtils";
import { generateNumericalId, toTitleCase } from "@utils/string";

import { EditTemplateModal } from "./EditTemplateModal";
import { TemplateTypeBadge } from "./TemplateTypeBadge";

type TemplateCardProps = {
  template: ReportGeneratorTemplateWithTypeSchema;
  allTemplates: ReportGeneratorTemplateWithTypeSchema[];
};

export const TemplateCard = ({ template, allTemplates }: TemplateCardProps) => {
  const dispatch = useDispatch();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const totalSections = template.sections?.length || 0;
  const createdDate = formatDateToLocal(template.created_at, "MMM d, yyyy");
  const numericalId = generateNumericalId(template.id);

  const deleteTemplate = useDeleteTemplate({
    onSuccess: () => {
      showSuccess("Template deleted successfully!", dispatch);
      setIsDeleteDialogOpen(false);
    },
    onError: (error) => {
      console.error("Failed to delete template:", error);
      showError("Failed to delete template. Please try again.", dispatch);
    },
  });

  return (
    <>
      <Card className="px-0">
        <CardHeader className="flex items-start justify-between px-6">
          <div className="flex-1 gap-2">
            <div className="flex items-center gap-3">
              <CardTitle className="text-lg">{template.name}</CardTitle>
              <TemplateTypeBadge isCustom={template.is_custom} />
            </div>
            <CardDescription className="flex items-center gap-4 pt-3 text-xs">
              <div className="flex items-center gap-1">
                <span>ID:</span>
                <span className="font-mono">{numericalId}</span>
              </div>
              {template.state && (
                <div className="flex items-center gap-1">
                  <StateIcon className="size-5" />
                  <span>State: {template.state}</span>
                </div>
              )}
              <div className="flex items-center gap-1">
                <SectionsIcon className="size-5" />
                <span>{totalSections} Sections</span>
              </div>
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 text-right text-sm text-foreground-muted">
              <Clock className="size-4 text-foreground-muted" />
              <span>Created: {createdDate}</span>
            </div>
          </div>
        </CardHeader>

        <CardContent className="border-t border-border px-6 pt-6">
          <Accordion type="single" collapsible>
            <AccordionItem value="sections" className="border-none">
              <div
                className={`flex items-center gap-2 ${template.is_custom ? "justify-between" : "justify-end"}`}
              >
                <AccordionTrigger className="w-auto justify-start rounded-md border border-border bg-background-white px-4 py-2.5 text-sm font-medium text-foreground hover:bg-background-muted hover:no-underline [&[data-state=open]>svg]:rotate-180 [&[data-state=open]]:bg-primary-50">
                  View Report Sections ({totalSections})
                </AccordionTrigger>
                {template.is_custom && (
                  <div className="flex gap-2 divide-x divide-border">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setIsEditModalOpen(true)}
                      className="gap-2 text-primary hover:text-primary"
                    >
                      <EditIcon className="size-4" />
                      Edit Sections
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setIsDeleteDialogOpen(true)}
                      className="gap-2 text-destructive hover:text-destructive"
                    >
                      <DeleteIcon className="size-4" />
                      Delete
                    </Button>
                  </div>
                )}
              </div>
              <AccordionContent className="pt-4">
                <Card className="p-4">
                  <h2 className="text-base font-semibold">
                    View Report Sections ({totalSections})
                  </h2>
                  <div className="flex flex-row flex-wrap gap-2">
                    {template.sections?.map((section, index) => (
                      <div
                        key={section.id}
                        className="inline-flex items-center gap-2 rounded-lg bg-background px-4 py-2 text-sm"
                      >
                        <span>{index + 1}.</span>
                        <span className="font-semibold text-foreground">
                          {toTitleCase(section.name)}
                        </span>
                      </div>
                    ))}
                  </div>
                </Card>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </CardContent>
      </Card>

      <EditTemplateModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        template={template}
        templates={allTemplates}
      />

      <ConfirmationModal
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        type="warning"
        icon={
          <img src={fileWithTrashIcon} alt="Delete template" className="size-12" />
        }
        title="Delete Selected Template?"
        description={`This action will permanently delete ${template.name} and cannot be undone. Please confirm to proceed.`}
        confirmButtonText={deleteTemplate.isPending ? "Deleting..." : "Yes, Delete"}
        cancelButtonText="No, Cancel"
        onConfirm={() => deleteTemplate.mutate(template.id)}
        onCancel={() => setIsDeleteDialogOpen(false)}
        isLoading={deleteTemplate.isPending}
      />
    </>
  );
};
